import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
});

// Add token to requests if it exists
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Use apiClient for authenticated requests, axios for public ones

// Conference APIs
export const getConferences = () => axios.get(`${API}/conferences`);
export const getConference = (id) => axios.get(`${API}/conferences/${id}`);
export const registerConference = (data) => axios.post(`${API}/conferences/register`, data);
export const registerForConference = registerConference; // Alias for compatibility

// Book APIs
export const getBooks = () => axios.get(`${API}/books`);
export const getBook = (id) => axios.get(`${API}/books/${id}`);
export const purchaseBook = (data) => axios.post(`${API}/books/purchase`, data);
export const getMyPurchases = (email) => axios.get(`${API}/books/purchases/my?email=${email}`);
export const downloadBook = (bookId, email) => {
  return axios.get(`${API}/books/download/${bookId}?email=${email}`, {
    responseType: 'blob'
  });
};

// Training APIs
export const getTrainings = (category) => {
  const url = category ? `${API}/trainings?category=${category}` : `${API}/trainings`;
  return axios.get(url);
};
export const getTraining = (id) => axios.get(`${API}/trainings/${id}`);
export const registerTraining = (data) => axios.post(`${API}/trainings/register`, data);

// MoreLife APIs
export const applyMoreLife = (data) => axios.post(`${API}/morelife/apply`, data);

// Project & Team APIs
export const getProjects = () => axios.get(`${API}/projects`);
export const getTeam = () => axios.get(`${API}/team`);

// Payment APIs
export const initializePayment = (data) => axios.post(`${API}/payment/initialize`, data);
export const verifyPayment = (data) => axios.post(`${API}/payment/verify`, data);

// Support APIs
export const createSupportTicket = (data) => axios.post(`${API}/support/tickets`, data);
export const submitContactForm = (data) => axios.post(`${API}/contact`, data);

// Authentication APIs
export const adminLogin = async (credentials) => {
  const response = await axios.post(`${API}/auth/login`, credentials);
  return response.data;
};

export const verifyAuth = async () => {
  const response = await apiClient.get('/auth/me');
  return response.data;
};

// Admin APIs (use apiClient for authenticated requests)
export const getDashboardStats = () => apiClient.get('/admin/dashboard');
export const getAllUsers = () => apiClient.get('/admin/users');
export const createUser = (data) => apiClient.post('/admin/users', data);
export const updateUser = (id, data) => apiClient.put(`/admin/users/${id}`, data);
export const deleteUser = (id) => apiClient.delete(`/admin/users/${id}`);

// Payment status management
export const updateConferencePaymentStatus = (id, status) => 
  apiClient.put(`/conferences/registrations/${id}/payment-status`, null, { params: { status } });
export const updateBookPaymentStatus = (id, status) => 
  apiClient.put(`/books/purchases/${id}/payment-status`, null, { params: { status } });
export const updateTrainingPaymentStatus = (id, status) => 
  apiClient.put(`/trainings/registrations/${id}/payment-status`, null, { params: { status } });

export const createConference = (data) => apiClient.post('/conferences', data);
export const updateConference = (id, data) => apiClient.put(`/conferences/${id}`, data);
export const deleteConference = (id) => apiClient.delete(`/conferences/${id}`);

export const createBook = (data) => apiClient.post('/books', data);
export const updateBook = (id, data) => apiClient.put(`/books/${id}`, data);
export const deleteBook = (id) => apiClient.delete(`/books/${id}`);

export const createTraining = (data) => apiClient.post('/trainings', data);
export const updateTraining = (id, data) => apiClient.put(`/trainings/${id}`, data);
export const deleteTraining = (id) => apiClient.delete(`/trainings/${id}`);

export const createProject = (data) => apiClient.post('/projects', data);
export const updateProject = (id, data) => apiClient.put(`/projects/${id}`, data);
export const deleteProject = (id) => apiClient.delete(`/projects/${id}`);

export const createTeamMember = (data) => apiClient.post('/team', data);
export const updateTeamMember = (id, data) => apiClient.put(`/team/${id}`, data);
export const deleteTeamMember = (id) => apiClient.delete(`/team/${id}`);

export const getConferenceRegistrations = () => apiClient.get('/conferences/registrations');
export const getTrainingRegistrations = () => apiClient.get('/trainings/registrations');
export const getBookPurchases = () => apiClient.get('/books/purchases');
export const getMoreLifeSessions = () => apiClient.get('/morelife/sessions');
export const getAllRegistrations = async () => {
  // Get all types of registrations
  const [conferences, trainings, books] = await Promise.all([
    getConferenceRegistrations(),
    getTrainingRegistrations(),
    getBookPurchases()
  ]);
  return {
    conferences: conferences.data,
    trainings: trainings.data,
    books: books.data
  };
};
export const getAllMessages = () => apiClient.get('/support/tickets');
export const scheduleMoreLifeSession = (id, scheduledDate) => 
  apiClient.put(`/morelife/sessions/${id}/schedule?scheduled_date=${scheduledDate}`);

export const getSupportTickets = () => apiClient.get('/support/tickets');
export const updateTicketStatus = (id, status) => 
  apiClient.put(`/support/tickets/${id}/status?status=${status}`);

export const getEmailCampaigns = () => apiClient.get('/email/campaigns');
export const createEmailCampaign = (data) => apiClient.post('/email/campaigns', data);
export const sendEmailCampaign = (id) => apiClient.post(`/email/campaigns/${id}/send`);